import React from 'react';
import { Link } from 'react-router-dom';
import {
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Code,
  ArrowRight,
  Facebook,
  Twitter,
  Instagram
} from 'lucide-react';
import { useQuery } from 'react-query';
import axios from 'axios';
const PaymentIcons = {
  mpesa: () => (
    <div className="flex items-center group/mpesa cursor-default">
      <div className="relative h-4 w-12 bg-transparent flex items-center justify-center transition-transform group-hover/mpesa:scale-105">
        <svg viewBox="0 0 160 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {/* Handset Outline */}
          <path d="M50 25h12v-8h3v8h3v35h-18z" fill="#d8e1d3" />
          <rect x="52" y="27" width="14" height="20" rx="1.5" fill="#fff" />

          {/* Bold Green Text */}
          <text x="5" y="52" fontFamily="Arial Black, sans-serif" fontSize="38" fontWeight="900" fill="#36ae42" letterSpacing="-2.5">M</text>
          <text x="75" y="52" fontFamily="Arial Black, sans-serif" fontSize="38" fontWeight="900" fill="#36ae42" letterSpacing="-2.5">PESA</text>

          {/* Red Swoosh */}
          <path d="M42 42 q15 -8 35 0 q-10 10 -35 0" fill="#ed1c24" />
        </svg>
      </div>
    </div>
  ),
  visa: () => (
    <div className="flex items-center group/visa cursor-default">
      <div className="w-8 h-4.5 bg-white rounded-sm flex flex-col items-stretch justify-between overflow-hidden border border-gray-100 shadow-sm transition-transform group-hover/visa:scale-105">
        <div className="h-[15%] bg-[#1a1f71]"></div>
        <div className="flex-1 flex items-center justify-center bg-white px-0.5">
          <svg viewBox="0 0 100 40" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <text x="50%" y="28" textAnchor="middle" fontFamily="Arial Black, sans-serif" fontSize="34" fontWeight="900" fill="#1a1f71" fontStyle="italic" letterSpacing="-1.5">VISA</text>
          </svg>
        </div>
        <div className="h-[15%] bg-[#f7b600]"></div>
      </div>
    </div>
  ),
  mastercard: () => (
    <div className="flex items-center group/mastercard cursor-default">
      <div className="w-7 h-4.5 bg-white rounded-sm flex items-center justify-center border border-gray-100 shadow-sm transition-transform group-hover/mastercard:scale-105">
        <svg viewBox="0 0 32 20" className="w-5 h-3" xmlns="http://www.w3.org/2000/svg">
          <circle cx="13" cy="10" r="7" fill="#eb001b" fillOpacity="0.85" />
          <circle cx="19" cy="10" r="7" fill="#ff5f00" fillOpacity="0.85" />
        </svg>
      </div>
    </div>
  ),
  stripe: () => (
    <div className="flex items-center group/stripe cursor-default">
      <div className="w-8 h-4.5 bg-white rounded-sm flex items-center justify-center border border-gray-100 shadow-sm transition-transform group-hover/stripe:scale-105">
        <svg viewBox="0 0 120 40" className="w-full h-full px-1" xmlns="http://www.w3.org/2000/svg">
          <text x="50%" y="28" textAnchor="middle" fontFamily="Arial Rounded MT Bold, sans-serif" fontSize="26" fontWeight="900" fill="#635bff">stripe</text>
        </svg>
      </div>
    </div>
  )
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const { data: globalSettings } = useQuery(
    'global-settings-app',
    () => axios.get('/api/settings').then(res => res.data.data),
    { staleTime: 1000 * 60 * 5 }
  );

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Portfolio', href: '/portfolio' },
      { name: 'Refer & Earn', href: '/refer' },
      { name: 'Testimonials', href: '/testimonials' },
      { name: 'Feedback', href: '/feedback' },
      { name: 'Contact', href: '/contact' }
    ],

    legal: [
      { name: 'Privacy Policy', href: '/privacy-policy' },
      { name: 'Terms of Service', href: '/terms-of-service' },
      { name: 'Cookie Policy', href: '/cookie-policy' },
      { name: 'Data Protection', href: '/data-protection' }
    ]
  };

  // Fetch Latest Version from Updates
  const { data: latestUpdate } = useQuery(
    'latest-platform-version',
    () => axios.get('/api/updates').then(res => res.data.data?.[0]),
    { staleTime: 1000 * 60 * 10 }
  );

  const displayVersion = latestUpdate?.version || '1.0.4';

  const socialLinks = [
    { icon: Facebook, href: globalSettings?.facebookUrl, label: 'Facebook' },
    { icon: Twitter, href: globalSettings?.twitterUrl, label: 'Twitter' },
    { icon: Instagram, href: globalSettings?.instagramUrl, label: 'Instagram' },
    { icon: Linkedin, href: globalSettings?.linkedinUrl, label: 'LinkedIn' }
  ].filter(link => link.href); // Only show links that have a URL set

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500/5 to-yellow-600/5 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-amber-500/5 to-yellow-600/5 rounded-full blur-3xl translate-x-48 translate-y-48"></div>

      <div className="relative z-10">
        {/* Main footer content */}
        {/* Main footer content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col gap-2">

            {/* Branding Section */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center">
                  <Code className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{globalSettings?.companyName || 'EpicEdge Creative'}</h3>
                  <p className="text-amber-400 text-xs font-medium">Modern Software Craftsmanship</p>
                </div>
              </div>
              {/* Contact Info + Social inline */}
              <div className="flex flex-row flex-wrap items-center gap-x-4 gap-y-1">
                <a href={`tel:${globalSettings?.companyPhone?.replace(/\s+/g, '') || '+254787205456'}`} className="flex items-center gap-1.5 text-gray-300 hover:text-amber-400 text-xs transition-colors duration-300">
                  <Phone className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <span>{globalSettings?.companyPhone || '+254 787 205 456'}</span>
                </a>
                <a href={`mailto:${globalSettings?.companyEmail || 'support@epicedgecreative.com'}`} className="flex items-center gap-1.5 text-gray-300 hover:text-amber-400 text-xs transition-colors duration-300">
                  <Mail className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <span>{globalSettings?.companyEmail || 'support@epicedgecreative.com'}</span>
                </a>
                <div className="flex items-center gap-1.5 text-gray-300 text-xs">
                  <MapPin className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <span>{globalSettings?.companyLocation || 'Nairobi, Kenya'}</span>
                </div>
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      aria-label={social.label}
                      className="w-5 h-5 bg-gray-800 hover:bg-gradient-to-br hover:from-amber-500 hover:to-yellow-600 rounded flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 hover:scale-110"
                    >
                      <Icon className="w-2.5 h-2.5" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Company */}
            <div className="flex flex-col items-center text-center my-1">
              <h4 className="text-xs font-bold text-white mb-2 relative inline-block">
                Company
                <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-gradient-to-r from-amber-500 to-yellow-600"></div>
              </h4>
              <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 max-w-2xl mx-auto">
                {footerLinks.company.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-amber-400 transition-colors duration-200 text-xs font-medium inline-block hover:scale-105 transition-transform"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* We Accept Row */}
            {globalSettings?.acceptedPaymentMethods?.length > 0 && (
              <div className="flex flex-col items-center text-center my-1">
                <h4 className="text-xs font-bold text-white mb-2 relative inline-block">
                  We Accept
                  <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-gradient-to-r from-amber-500 to-yellow-600"></div>
                </h4>
                <div className="flex flex-wrap items-center justify-center gap-2.5 mt-1">
                  {globalSettings.acceptedPaymentMethods.includes('mpesa') && <PaymentIcons.mpesa />}
                  {globalSettings.acceptedPaymentMethods.includes('stripe') && <PaymentIcons.stripe />}
                  {(globalSettings.acceptedPaymentMethods.includes('visa') || globalSettings.acceptedPaymentMethods.includes('mastercard') || globalSettings.acceptedPaymentMethods.includes('bank_transfer')) && (
                    <>
                      <PaymentIcons.visa />
                      <PaymentIcons.mastercard />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>


        {/* Bottom Bar */}
        <div className="border-t border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex flex-row flex-wrap items-center justify-between gap-1">
              <div className="text-gray-300 text-xs flex items-center gap-2">
                <p>© {currentYear} EpicEdge Creative. All rights reserved.</p>
                <span className="text-[10px] text-gray-500 opacity-50 px-1 border border-gray-700 rounded uppercase">V{displayVersion}</span>
              </div>

              <div className="flex gap-4 text-xs">
                {footerLinks.legal.map((link, index) => (
                  <Link
                    key={index}
                    to={link.href}
                    className="text-gray-300 hover:text-amber-400 transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
